import React, { useEffect, useState, useCallback } from 'react';
import {
  getTemplate, saveTemplateConfig,
  addTemplateProduct, updateTemplateProduct, deleteTemplateProduct,
} from '../../services/api';
import { Input, Textarea, Btn, toast, useConfirm, Card, Spinner } from '../../components/shared/UI';
import ProductForm from '../../components/shared/ProductForm';

const MAX = 30;

export default function AdminTemplate() {
  const [tpl, setTpl]         = useState(null);
  const [cfgLoad, setCfgLoad] = useState(false);
  const [prodModal, setProdModal] = useState(null); // null | 'new' | product
  const [prodSaving, setProdSaving] = useState(false);
  const { confirm, Dialog }   = useConfirm();

  const load = useCallback(() => {
    getTemplate().then(setTpl).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const setConfig = (k, v) => setTpl(p => ({ ...p, config: { ...p.config, [k]: v } }));

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setCfgLoad(true);
    try {
      await saveTemplateConfig(tpl.config);
      toast('Configuración guardada ✅');
    } catch (err) { toast(err.message, 'error'); }
    finally { setCfgLoad(false); }
  };

  const handleSaveProduct = async (data) => {
    setProdSaving(true);
    try {
      if (prodModal === 'new') {
        await addTemplateProduct(data);
        toast('Producto agregado ✅');
      } else {
        await updateTemplateProduct(prodModal.id, data);
        toast('Producto actualizado ✅');
      }
      setProdModal(null);
      load();
    } catch (err) { toast(err.message, 'error'); }
    finally { setProdSaving(false); }
  };

  const handleDelete = async (p) => {
    const ok = await confirm(`¿Eliminar "${p.nombre}" de la plantilla?`);
    if (!ok) return;
    try {
      await deleteTemplateProduct(p.id);
      toast('Producto eliminado');
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  if (!tpl) return (
    <div style={{ display:'flex',justifyContent:'center',padding:60 }}><Spinner /></div>
  );

  const productos = tpl.productos || [];

  return (
    <div>
      <Dialog />
      <ProductForm
        open={!!prodModal}
        onClose={() => setProdModal(null)}
        onSave={handleSaveProduct}
        initial={prodModal && prodModal !== 'new' ? prodModal : null}
        loading={prodSaving}
      />

      <div style={{ marginBottom:28 }}>
        <h1 style={{ color:'#fff',fontSize:24,fontFamily:"'Syne',sans-serif",fontWeight:800,margin:'0 0 6px' }}>Plantilla Base</h1>
        <p style={{ color:'rgba(255,255,255,0.38)',fontSize:14,margin:0 }}>
          Los nuevos clientes heredarán esta configuración y productos automáticamente.
        </p>
      </div>

      {/* Config */}
      <Card style={{ marginBottom:20 }}>
        <h2 style={{ color:'#fff',fontSize:16,fontWeight:600,marginBottom:20,margin:'0 0 20px' }}>⚙️ Configuración de la plantilla</h2>
        <form onSubmit={handleSaveConfig}>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:12 }}>
            <Input label="Nombre de la tienda" value={tpl.config?.nombre || ''} onChange={e => setConfig('nombre', e.target.value)} />
            <Input label="Moneda" value={tpl.config?.moneda || 'ARS'} onChange={e => setConfig('moneda', e.target.value)} placeholder="ARS" />
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block',color:'rgba(255,255,255,0.55)',fontSize:13,fontWeight:500,marginBottom:6 }}>Color principal</label>
              <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                <input type="color" value={tpl.config?.color || '#7c3aed'} onChange={e => setConfig('color', e.target.value)}
                  style={{ width:44,height:38,padding:2,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,cursor:'pointer' }} />
                <span style={{ color:'rgba(255,255,255,0.4)',fontSize:13 }}>{tpl.config?.color || '#7c3aed'}</span>
              </div>
            </div>
            <Input label="WhatsApp (solo dígitos)" value={tpl.config?.whatsapp || ''} onChange={e => setConfig('whatsapp', e.target.value)} placeholder="5491122334455" />
          </div>
          <Textarea label="Descripción" value={tpl.config?.descripcion || ''} onChange={e => setConfig('descripcion', e.target.value)} />
          <div style={{ display:'flex',justifyContent:'flex-end' }}>
            <Btn type="submit" loading={cfgLoad}>Guardar configuración</Btn>
          </div>
        </form>
      </Card>

      {/* Products */}
      <Card>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,flexWrap:'wrap',gap:10 }}>
          <div>
            <h2 style={{ color:'#fff',fontSize:16,fontWeight:600,margin:'0 0 3px' }}>📦 Productos de la plantilla</h2>
            <p style={{ color:'rgba(255,255,255,0.35)',fontSize:12,margin:0 }}>{productos.length}/{MAX} productos</p>
          </div>
          {productos.length < MAX && (
            <Btn size="sm" onClick={() => setProdModal('new')}>+ Agregar producto</Btn>
          )}
        </div>

        {productos.length === 0 ? (
          <div style={{ textAlign:'center',padding:'32px 0',color:'rgba(255,255,255,0.3)',fontSize:14 }}>
            No hay productos en la plantilla todavía.
          </div>
        ) : (
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12 }}>
            {productos.map(p => (
              <div key={p.id} style={{ background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:12,overflow:'hidden' }}>
                <div style={{ height:110,background:'rgba(255,255,255,0.04)',overflow:'hidden' }}>
                  {p.imagen ? (
                    <img src={p.imagen} alt={p.nombre} style={{ width:'100%',height:'100%',objectFit:'cover' }} onError={e => { e.target.style.display='none'; }} />
                  ) : (
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%',fontSize:32,color:'rgba(255,255,255,0.15)' }}>👟</div>
                  )}
                </div>
                <div style={{ padding:'11px 12px' }}>
                  <div style={{ color:'rgba(255,255,255,0.42)',fontSize:11,marginBottom:2 }}>{p.categoria || '—'}</div>
                  <div style={{ color:'#fff',fontWeight:600,fontSize:13,marginBottom:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{p.nombre}</div>
                  <div style={{ color:'#a78bfa',fontWeight:700,fontSize:14,marginBottom:10 }}>${p.precio?.toLocaleString('es-AR')}</div>
                  <div style={{ display:'flex',gap:6 }}>
                    <Btn size="sm" variant="ghost" style={{ flex:1 }} onClick={() => setProdModal(p)}>✏️ Editar</Btn>
                    <Btn size="sm" variant="danger" onClick={() => handleDelete(p)}>🗑</Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
